import { calculateBoundingBox } from "@nova/calculate-geospatial"
import { Polygon } from "@nova/protobuf"
import { escapeXmlEntities } from "@nova/util-strings"

export type Point = [number, number]

const createCoordinate = ([longitude, latitude]: Point) => {
    return `${longitude},${latitude},${0}`
}

const idGenerator = () => {
    let i = 1
    const time = Date.now().toString(36)

    return { getId: () => `polygon-${i++}-${time}`, time }
}

export function createPolygonKML(
    name: string,
    polygons: { coords: Point[]; width?: number; color?: string; fill?: string }[]
) {
    const mutablePolygons = polygons.filter((polygon) => polygon.coords.length >= 3)

    mutablePolygons.forEach(({ coords, width, color, fill }, p) => {
        // Duplicate first vertex and append to create complete shape path.
        if (!coords[0].every((e, i) => e === coords[coords.length - 1][i]))
            mutablePolygons[p] = { coords: [...coords, coords[0]], width, color, fill }
    })

    const { getId } = idGenerator()
    const escapedName = escapeXmlEntities(name)

    return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${escapedName}</name>
        ${mutablePolygons
            .map(({ coords, width, color, fill }, i) => {
                return `
        <Style id="poly${i}">
            <LineStyle>
                <color>${color || "FF1A1AE5"}</color>
                <width>${!width || width === 1 ? 2 : 4}</width>
            </LineStyle>
            <PolyStyle>
            ${fill ? `<color>${fill}</color>` : "<fill>0</fill>"}
            </PolyStyle>
        </Style>
        <Placemark id="${getId()}">
            <name>${escapedName}</name>
            <styleUrl>#poly${i}</styleUrl>
            <Polygon>
                <tessellate>1</tessellate>
                <outerBoundaryIs>
                    <LinearRing>
                        <coordinates>
                            ${coords.map(createCoordinate).join("\n")}
                        </coordinates>
                    </LinearRing>
                </outerBoundaryIs>
            </Polygon>
        </Placemark>`
            })
            .join("\n")}
  </Document>
</kml>`
}

export const createPolygonGPX = (name: string, polygons: Polygon[]) => {
    const { bbox } = calculateBoundingBox(polygons.flatMap(({ coordinates }) => coordinates))
    const escapedName = escapeXmlEntities(name)
    return `<?xml version="1.0"?>
<gpx version="1.1" creator="GDAL 3.6.3"
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
  xmlns:ogr="http://osgeo.org/gdal"
  xmlns="http://www.topografix.com/GPX/1/1" xsi:schemaLocation="http://www.topografix.com/GPX/1/1 http://www.topografix.com/GPX/1/1/gpx.xsd">
  <metadata>
      <bounds minlat="${bbox.minlat}" minlon="${bbox.minlng}" maxlat="${bbox.maxlat}" maxlon="${bbox.maxlng}"/>
  </metadata>
  ${polygons
      .map(
          (polygon) => `<trk>
      <name>${escapedName}</name>
      <trkseg>
${polygon.coordinates
    .map(
        ({ longitude, latitude }) =>
            `            <trkpt lat="${latitude}" lon="${longitude}">
              <ele>0.0</ele>
          </trkpt>            `
    )
    .join("\n")}
      </trkseg>
  </trk>`
      )
      .join("\n")}
</gpx>`
}
